/**
 * App — M5 viewer UI with passkey unlock and user switcher.
 *
 * Orchestrates the unlock flow and renders the multi-screen PWA
 * once the artifact is decrypted.
 */

import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ArtifactEnvelopeSchema, type ArtifactBody, type Cents } from '@pf/contract';
import { loadArtifact } from './artifact/load.js';
import { ArtifactProvider } from './context/ArtifactContext.js';
import { AppShell } from './nav/AppShell.js';
import { UnlockScreen } from './unlock/Unlock.js';
import { SkeletonCard, SkeletonHero } from './ui/Skeleton.js';
import { USERS } from './users.js';
import type { ArtifactEnvelope } from '@pf/contract';

// ─── Lazy-loaded screens for bundle splitting ─────────────────────

const Dashboard     = lazy(() => import('./screens/Dashboard.js').then((m) => ({ default: m.Dashboard })));
const Categories    = lazy(() => import('./screens/Categories.js').then((m) => ({ default: m.Categories })));
const CategoryDetail = lazy(() => import('./screens/CategoryDetail.js').then((m) => ({ default: m.CategoryDetail })));
const Transactions  = lazy(() => import('./screens/Transactions.js').then((m) => ({ default: m.Transactions })));
const Loans         = lazy(() => import('./screens/Loans.js').then((m) => ({ default: m.Loans })));
const Trends        = lazy(() => import('./screens/Trends.js').then((m) => ({ default: m.Trends })));
const Settings      = lazy(() => import('./screens/Settings.js').then((m) => ({ default: m.Settings })));

// ─── Net position (for legacy tests) ──────────────────────────────

export function calcNetPosition(body: ArtifactBody): Cents {
  const accountTotal = body.accounts.reduce(
    (sum: number, acc) => sum + (acc.closingBalanceCents as number),
    0,
  );
  const loanTotal = body.loans.reduce(
    (sum: number, loan) => sum + (loan.currentBalanceCents as number),
    0,
  );
  return (accountTotal - loanTotal) as Cents;
}

// ─── Active user persistence ──────────────────────────────────────

const ACTIVE_USER_KEY = 'viewer.activeUserId';
const DEFAULT_USER_ID = USERS[0]?.userId ?? 'FAKE-USER-001';

function getActiveUserId(): string {
  return localStorage.getItem(ACTIVE_USER_KEY) ?? DEFAULT_USER_ID;
}

function setActiveUserId(userId: string): void {
  localStorage.setItem(ACTIVE_USER_KEY, userId);
}

function getArtifactPath(userId: string): string {
  return USERS.find((u) => u.userId === userId)?.artifactPath
    ?? `/artifacts/${userId}.artifact`;
}

// ─── App states ───────────────────────────────────────────────────

type AppState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'prompt'; rawEnvelope: unknown }
  | { kind: 'decrypting'; rawEnvelope: unknown }
  | { kind: 'done'; body: ArtifactBody; envelope: ArtifactEnvelope; sessionKey: Uint8Array };

function ScreenFallback() {
  return (
    <div className="px-4 py-6 space-y-4 max-w-2xl mx-auto">
      <SkeletonHero />
      <SkeletonCard />
      <SkeletonCard lines={4} />
    </div>
  );
}

// ─── App component ────────────────────────────────────────────────

export function App() {
  const [state, setState] = useState<AppState>({ kind: 'loading' });
  const [activeUserId, setActiveUserIdState] = useState<string>(getActiveUserId);

  // Fetch artifact envelope for the active user on mount or user change
  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    (async () => {
      try {
        const artifactPath = getArtifactPath(activeUserId);
        const res = await globalThis.fetch(artifactPath);
        if (!res.ok) {
          throw new Error(`Failed to fetch artifact: ${res.status} ${res.statusText}`);
        }
        const raw: unknown = await res.json();
        ArtifactEnvelopeSchema.parse(raw);
        if (!cancelled) setState({ kind: 'prompt', rawEnvelope: raw });
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeUserId]);

  async function handlePassphraseUnlock(passphrase: string) {
    if (state.kind !== 'prompt') return;
    const envelope = state.rawEnvelope;
    setState({ kind: 'decrypting', rawEnvelope: envelope });
    try {
      const { body, envelope: parsedEnvelope, key } = await loadArtifact(
        envelope,
        { kind: 'passphrase', passphrase },
      );
      setState({ kind: 'done', body, envelope: parsedEnvelope, sessionKey: key });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState((prev) => ({
        ...prev,
        kind: 'prompt',
        decryptError: message,
      } as AppState & { decryptError?: string }));
    }
  }

  async function handlePasskeyUnlock(key: Uint8Array) {
    if (state.kind !== 'prompt') return;
    const envelope = state.rawEnvelope;
    setState({ kind: 'decrypting', rawEnvelope: envelope });
    try {
      const { body, envelope: parsedEnvelope, key: resolvedKey } = await loadArtifact(
        envelope,
        { kind: 'key', key },
      );
      setState({ kind: 'done', body, envelope: parsedEnvelope, sessionKey: resolvedKey });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState((prev) => ({
        ...prev,
        kind: 'prompt',
        decryptError: message,
      } as AppState & { decryptError?: string }));
    }
  }

  function lock() {
    // Clear decrypted data and re-fetch the envelope
    setState({ kind: 'loading' });
    (async () => {
      try {
        const artifactPath = getArtifactPath(activeUserId);
        const res = await globalThis.fetch(artifactPath);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const raw: unknown = await res.json();
        ArtifactEnvelopeSchema.parse(raw);
        setState({ kind: 'prompt', rawEnvelope: raw });
      } catch (err) {
        setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    })();
  }

  function switchUser(userId: string) {
    // Re-lock (clear decrypted artifact) and switch user
    setActiveUserId(userId);
    setActiveUserIdState(userId);
    // The useEffect above will re-fetch the new user's artifact
  }

  // ─── Loading state ──────────────────────────────────────────────

  if (state.kind === 'loading') {
    return (
      <main className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <p className="text-[var(--color-text-secondary)] text-sm animate-pulse">
          Loading artifact…
        </p>
      </main>
    );
  }

  // ─── Error state ────────────────────────────────────────────────

  if (state.kind === 'error') {
    return (
      <main className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center px-6 gap-4">
        <p
          role="alert"
          className="text-[var(--color-negative)] text-sm text-center max-w-sm"
        >
          {state.message}
        </p>
        <button
          onClick={() => setState({ kind: 'loading' })}
          className="text-sm text-[var(--color-accent)] underline min-h-[44px]"
        >
          Try again
        </button>
      </main>
    );
  }

  // ─── Unlock / decrypting ────────────────────────────────────────

  if (state.kind === 'prompt' || state.kind === 'decrypting') {
    const decryptError =
      'decryptError' in state
        ? (state as unknown as { decryptError: string }).decryptError
        : undefined;

    return (
      <UnlockScreen
        userId={activeUserId}
        onPassphraseUnlock={handlePassphraseUnlock}
        onPasskeyUnlock={handlePasskeyUnlock}
        error={decryptError}
        loading={state.kind === 'decrypting'}
      />
    );
  }

  // ─── Done — full app ────────────────────────────────────────────

  return (
    <ArtifactProvider
      body={state.body}
      envelope={state.envelope}
      sessionKey={state.sessionKey}
      lock={lock}
      switchUser={switchUser}
      activeUserId={activeUserId}
    >
      <BrowserRouter>
        <Suspense fallback={<ScreenFallback />}>
          <Routes>
            <Route element={<AppShell />}>
              <Route index element={<Dashboard />} />
              <Route path="categories" element={<Categories />} />
              <Route path="categories/:categoryId" element={<CategoryDetail />} />
              <Route path="transactions" element={<Transactions />} />
              <Route path="loans" element={<Loans />} />
              <Route path="trends" element={<Trends />} />
              <Route path="settings" element={<Settings />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ArtifactProvider>
  );
}

// Keep net position and format helpers on the App module for backward-compatibility with M1 tests
export function formatAud(cents: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(cents / 100);
}
